import {
  SkyChartBlock,
  SkyDataGrid,
  SkyDrawer,
  SkyDropdown,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useState, type CSSProperties, type ReactNode } from "react";
import {
  CHECK_KIND_LABELS,
  type MonitorCheckRow,
  type MonitorObservation,
} from "@shared/types/monitor";
import { ErrorState, LoadingRows } from "@/components/access/ScreenStates";
import { useMonitorCheck } from "@/hooks/use-monitor";
import { pixelColumnLayout } from "@/lib/grid";
import { countWithBasis, formatAmount, type WatchedTable } from "@/lib/monitor/format";
import { MonitorStatusBadge } from "./MonitorStatusBadge";
import {
  chartTimeLabels,
  formatInstant,
  instantHoverTitle,
  zoneCaption,
} from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * One table's history (spec section 11), in a drawer so the list stays in view: pick one of the
 * table's checks, see its observed value against the expected band over the last 30 days, and the
 * observations below, newest first, 14 to a page. Numbers only; no row of the table reaches this
 * screen.
 */

const LAST_OBSERVATIONS = 14;

const STATUS_WORDS = {
  pass: "Passed",
  breach: "Breached",
  training: "Training",
  paused: "Paused",
  unknown: "Unknown",
} as const;

/**
 * Round 3 (owner, 2026-09-30): the drawer's grid was percentages only, so the check time had 188
 * px for a 191 px time. Measured in Chromium in both fonts (client/AGENTS.md), border-box with the
 * cell's 24 px padding: the time, in the reader's display zone (196 px), is one line; Found,
 * Expected and Result share the rest in proportion to a minimum that holds their header. The
 * floor, 552 px, is the large drawer's pane; a longer value wraps in its cell rather than being
 * cut.
 */
const CHECKED_WIDTH = 200;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  value: 112,
  expectedHigh: 144,
  status: 96,
};

/** An observation's expected range: "10 to 20", "At most 20", or "Not set". */
function expectedWords(row: MonitorObservation, unit: MonitorCheckRow["unit"]): string {
  if (row.expectedLow !== null && row.expectedHigh !== null) {
    return `${formatAmount(row.expectedLow, unit)} to ${formatAmount(row.expectedHigh, unit)}`;
  }
  if (row.expectedHigh !== null) return `At most ${formatAmount(row.expectedHigh, unit)}`;
  return "Not set";
}

function observationColumns(
  check: MonitorCheckRow,
  zone: string,
): SkyDataGridColumn<MonitorObservation>[] {
  return [
    {
      key: "observedAt",
      header: "Checked",
      width: `${CHECKED_WIDTH}px`,
      renderCell: (_value, row) => (
        <SkyText size={300} title={instantHoverTitle(row.observedAt, zone)}>
          {formatInstant(row.observedAt, zone, "minutes")}
        </SkyText>
      ),
    },
    {
      key: "value",
      header: "Found",
      width: "22%",
      renderCell: (_value, row) => (
        // #1281: a row count says whether it is the rows the load brought or the rows in total.
        <SkyText size={300} font="numeric">
          {countWithBasis(row.value, check.unit, row.basis)}
        </SkyText>
      ),
    },
    {
      key: "expectedHigh",
      header: "Expected",
      width: "24%",
      renderCell: (_value, row) => (
        <SkyText size={300} font="numeric" color="muted">
          {expectedWords(row, check.unit)}
        </SkyText>
      ),
    },
    {
      key: "status",
      header: "Result",
      width: "20%",
      renderCell: (_value, row) => <SkyText size={300}>{STATUS_WORDS[row.status]}</SkyText>,
    },
  ];
}

/** "Connection · entity": the connection, and the connector's own name when the table differs. */
function drawerSubtitle(table: WatchedTable): string {
  return [
    table.connectionName ?? "Deleted connection",
    // Round 6 W15: the connector's own name, under the warehouse table's.
    ...(table.tableName !== null && table.tableName !== table.entityName ? [table.entityName] : []),
  ].join(" · ");
}

/**
 * #1281 review: a count changed what it counts when #1279 shipped (a full or window table's Zero
 * rows count was its total, now the rows each load brought), so one line through both would show a
 * drop that never happened. The chart plots only the observations that count what the newest one
 * counts. The grid below still lists every observation, each with its own basis. Null when nothing
 * is left out.
 */
function chartedObservations(plotted: MonitorObservation[]): {
  charted: MonitorObservation[];
  basis: NonNullable<MonitorObservation["basis"]> | null;
} {
  const basis = plotted.at(-1)?.basis ?? null;
  if (basis === null) return { charted: plotted, basis: null };
  const charted = plotted.filter((row) => row.basis === basis);
  return charted.length === plotted.length ? { charted: plotted, basis: null } : { charted, basis };
}

/** The chart's caption when some observations count something else and are left out of it. */
function leftOutCaption(basis: NonNullable<MonitorObservation["basis"]>): string {
  const counted = basis === "loaded" ? "the rows each load brought" : "the rows in total";
  return ` The chart shows ${counted}. Older counts of another kind are in the table below.`;
}

/** The chart's lines: what was found, and the expected limits when the check has any. */
function observationSeries(plotted: MonitorObservation[]) {
  return [
    { name: "Found", values: plotted.map((row) => row.value ?? 0) },
    ...(plotted.some((row) => row.expectedHigh !== null)
      ? [{ name: "Upper limit", values: plotted.map((row) => row.expectedHigh ?? 0) }]
      : []),
    ...(plotted.some((row) => row.expectedLow !== null)
      ? [{ name: "Lower limit", values: plotted.map((row) => row.expectedLow ?? 0) }]
      : []),
  ];
}

export function MonitorTableDrawer({
  table,
  onClose,
}: {
  table: WatchedTable | null;
  onClose: () => void;
}) {
  const zone = useDisplayTz();
  const [chosen, setChosen] = useState<string | null>(null);
  const checks = table?.checks ?? [];
  const selected =
    checks.find((check) => check.id === chosen) ?? table?.freshness ?? checks[0] ?? null;
  const detail = useMonitorCheck(selected?.id ?? null);
  const observations = detail.data?.observations ?? [];
  // Newest first, 14 to a page: the grid is where every point's full time is read, because a
  // multi-day chart labels its points (hover and Table view too) with the day only.
  const newestFirst = [...observations].reverse();
  const { charted: plotted, basis: chartBasis } = chartedObservations(
    observations.filter((row) => row.value !== null),
  );
  const layout = pixelColumnLayout(selected ? observationColumns(selected, zone) : [], {
    minimums: MIN_COLUMN_WIDTH,
    selection: false,
  });

  function renderHistory(current: NonNullable<typeof selected>): ReactNode {
    if (detail.isPending) {
      return <LoadingRows rows={4} columns={4} />;
    }
    if (detail.isError) {
      return (
        <ErrorState
          title="This monitor's history could not be read"
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      );
    }
    if (observations.length === 0) {
      return (
        <SkyText size={300} color="muted" block testId="monitor-drawer-empty">
          No observation yet. skyMonitor records one each time the value or the result changes.
        </SkyText>
      );
    }
    return (
      <>
        <SkyChartBlock
          title={`${CHECK_KIND_LABELS[current.kind]}, last 30 days`}
          description={
            chartBasis === null
              ? zoneCaption(zone)
              : `${zoneCaption(zone)}.${leftOutCaption(chartBasis)}`
          }
          type="line"
          categories={chartTimeLabels(
            plotted.map((row) => row.observedAt),
            zone,
          )}
          series={observationSeries(plotted)}
          formatValue={(value) => formatAmount(value, current.unit)}
          height={200}
          testId="monitor-drawer-chart"
        />
        <div style={{ "--monitor-drawer-floor": `${layout.floor}px` } as CSSProperties}>
          <SkyDataGrid<MonitorObservation>
            data={newestFirst}
            columns={layout.columns}
            getRowId={(row) => row.observedAt}
            ariaLabel="Last observations"
            className="[&_table]:min-w-(--monitor-drawer-floor)"
            testId="monitor-drawer-observations"
            pagination={newestFirst.length > LAST_OBSERVATIONS}
            pageSize={LAST_OBSERVATIONS}
          />
        </div>
      </>
    );
  }

  return (
    <SkyDrawer
      open={table !== null}
      onOpenChange={(open) => {
        if (!open) {
          setChosen(null);
          onClose();
        }
      }}
      headerTitle={table?.displayName ?? ""}
      subtitle={table ? drawerSubtitle(table) : undefined}
      size="large"
      testId="monitor-table-drawer"
    >
      {selected ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-64">
              <SkyDropdown
                label="Monitor"
                testId="monitor-drawer-check"
                selectedOptions={[selected.id]}
                options={checks.map((check) => ({
                  value: check.id,
                  text: CHECK_KIND_LABELS[check.kind],
                }))}
                onOptionSelect={(values) => setChosen(values[0] ?? null)}
              />
            </div>
            <MonitorStatusBadge status={selected.status} testId="monitor-drawer-status" />
          </div>
          {renderHistory(selected)}
        </div>
      ) : null}
    </SkyDrawer>
  );
}
